import React from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Icon,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaRocket,
  FaUsers,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { demoConfig } from '../config/demoConfig';

const ContactPage: React.FC = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const { t } = useTranslation();

  const contactMethods = [
    {
      title: t('contactPage.methods.sales.title'),
      description: t('contactPage.methods.sales.description'),
      icon: FaRocket,
      contact: 'info@creatix.tech',
      phones: ['+374 93 683726'],
      response: t('contactPage.methods.sales.response'),
      badge: t('contactPage.methods.sales.badge'),
      badgeColor: 'green',
    },
    {
      title: t('contactPage.methods.partnership.title'),
      description: t('contactPage.methods.partnership.description'),
      icon: FaUsers,
      contact: 'info@creatix.tech',
      phones: ['+374 95 884838'],
      response: t('contactPage.methods.partnership.response'),
      badge: t('contactPage.methods.partnership.badge'),
      badgeColor: 'purple',
    },
  ];

  const officeLocations = [
    {
      city: 'Yerevan',
      country: 'Armenia',
      address: '24/15 Azatutyan Ave, 0018 Yerevan, Armenia',
      icon: FaMapMarkerAlt,
    },
    {
      city: 'Berlin',
      country: 'Germany',
      address: 'Schönhauser Allee 163, 10435 Berlin, Germany',
      icon: FaMapMarkerAlt,
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box bg={bgColor} py={{ base: 8, md: 10 }}>
        <Container maxW='7xl'>
          <VStack spacing={{ base: 4, md: 6 }} textAlign='center'>
            <Heading size={{ base: 'xl', md: '2xl' }} color='primary.500'>
              {t('contact.title')}
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color='gray.500'
              maxW='3xl'
            >
              {t('contact.subtitle')}
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Contact Methods */}
      <Box bg={bgColor} py={{ base: 8, md: 10 }}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={8}
              maxW='4xl'
              mx='auto'
            >
              {contactMethods.map((method, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={8}
                  borderRadius='lg'
                  boxShadow='md'
                  border='1px solid'
                  borderColor={useColorModeValue('gray.200', 'gray.600')}
                  position='relative'
                  display='flex'
                  flexDirection='column'
                  h='full'
                >
                  <VStack spacing={4} align='start' flex={1} w='full'>
                    <HStack justify='space-between' w='full'>
                      <Icon as={method.icon} boxSize={8} color='primary.500' />
                      <Badge colorScheme={method.badgeColor} variant='solid'>
                        {method.badge}
                      </Badge>
                    </HStack>

                    <Heading
                      color={useColorModeValue('gray.900', 'white')}
                      size='md'
                    >
                      {method.title}
                    </Heading>
                    <Text
                      color={useColorModeValue('gray.600', 'gray.400')}
                      fontSize='sm'
                    >
                      {method.description}
                    </Text>

                    <VStack spacing={2} align='start' w='full'>
                      <HStack spacing={2}>
                        <Icon as={FaEnvelope} boxSize={4} color='gray.500' />
                        <Text
                          color={useColorModeValue('gray.800', 'white')}
                          fontSize='sm'
                          fontWeight='medium'
                        >
                          {method.contact}
                        </Text>
                      </HStack>

                      {method.phones.map((phone, idx) => (
                        <HStack key={idx} spacing={2}>
                          <Icon as={FaPhone} boxSize={4} color='gray.500' />
                          <Text
                            color={useColorModeValue('gray.800', 'white')}
                            fontSize='sm'
                            fontWeight='medium'
                          >
                            {phone}
                          </Text>
                        </HStack>
                      ))}

                      <HStack spacing={2}>
                        <Icon as={FaClock} boxSize={4} color='gray.500' />
                        <Text
                          color={useColorModeValue('gray.600', 'gray.400')}
                          fontSize='sm'
                        >
                          {t('contactPage.labels.response')}: {method.response}
                        </Text>
                      </HStack>
                    </VStack>

                    <Button
                      alignSelf='stretch'
                      colorScheme='primary'
                      mt='auto'
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg',
                      }}
                      onClick={() =>
                        window.open(demoConfig.contactSales, '_blank')
                      }
                    >
                      {t('contactPage.labels.contact')} {method.title}
                    </Button>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Office Locations */}
      <Box bg={bgColor} py={{ base: 12, md: 8 }}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading size='xl' color={useColorModeValue('gray.900', 'white')}>
                {t('contactPage.offices.title')}
              </Heading>
              <Text fontSize='lg' color='gray.500' maxW='2xl'>
                {t('contactPage.offices.subtitle')}
              </Text>
            </VStack>

            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={8}
              maxW='4xl'
              mx='auto'
            >
              {officeLocations.map((office, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={6}
                  borderRadius='lg'
                  boxShadow='md'
                  border='1px solid'
                  borderColor={useColorModeValue('gray.200', 'gray.600')}
                >
                  <VStack spacing={4} align='start'>
                    <HStack spacing={3}>
                      <Icon as={office.icon} boxSize={6} color='primary.500' />
                      <VStack spacing={0} align='start'>
                        <Text
                          color={useColorModeValue('gray.900', 'white')}
                          fontWeight='bold'
                          fontSize='lg'
                        >
                          {office.city}
                        </Text>
                        <Text
                          fontSize='sm'
                          color={useColorModeValue('gray.500', 'gray.400')}
                        >
                          {office.country}
                        </Text>
                      </VStack>
                    </HStack>

                    <Text
                      fontSize='sm'
                      color={useColorModeValue('gray.600', 'gray.500')}
                    >
                      {office.address}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      {/* <Box py={{ base: 12, md: 16 }}>
        <Container maxW='4xl'>
          <Box
            bg='primary.500'
            color='white'
            p={{ base: 6, md: 8 }}
            borderRadius='xl'
            textAlign='center'
            w='full'
            maxW='4xl'
          >
            <VStack spacing={{ base: 4, md: 6 }}>
              <Heading size={{ base: 'md', md: 'lg' }}>
                Ready to Get Started?
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} maxW='2xl'>
                Join leading poker operators who trust our solutions. Schedule a
                consultation to discuss your requirements and see our platform
                in action.
              </Text>
              <VStack spacing={4} w={{ base: 'full', md: 'auto' }}>
                <HStack
                  spacing={4}
                  flexWrap={{ base: 'wrap', md: 'nowrap' }}
                  justify='center'
                >
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    variant='solid'
                    bg='white'
                    color='primary.500'
                    _hover={{
                      bg: 'gray.100',
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg',
                    }}
                    w={{ base: 'full', sm: 'auto' }}
                    minW={{ base: 'auto', sm: '180px' }}
                  >
                    Schedule Demo
                  </Button>
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    variant='outline'
                    borderColor='white'
                    color='white'
                    _hover={{
                      bg: 'whiteAlpha.200',
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg',
                    }}
                    w={{ base: 'full', sm: 'auto' }}
                    minW={{ base: 'auto', sm: '180px' }}
                  >
                    Download Brochure
                  </Button>
                </HStack>
              </VStack>
            </VStack>
          </Box>
        </Container>
      </Box> */}
    </Box>
  );
};

export default ContactPage;
